"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import ActionMenu from "@/app/(components)/generics/ActionMenu/ActionMenu";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import IconEdit from "@/app/(components)/generics/Icons/IconEdit";
import IconPlus from "@/app/(components)/generics/Icons/IconPlus";
import IconTrash from "@/app/(components)/generics/Icons/IconTrash";
import { SelectInput } from "@/app/(components)/generics/inputs/default/SelectInput/SelectInput";
import ToggleInput from "@/app/(components)/generics/inputs/default/ToggleInput/ToggleInput";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import ScriptBlock from "@/app/(components)/specifics/tools/MessagesContent/components/Scripts/components/ScriptBlock/ScriptBlock";
import ScriptStep from "@/app/(components)/specifics/tools/MessagesContent/components/Scripts/components/ScriptStep/ScriptStep";
import {
  useCanEditContent,
  useCanManageWorkspace,
} from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import {
  refreshScriptSources,
  saveScriptBlockBody,
  saveScriptBlockItems,
  saveScriptItemForInsight,
  saveScriptItemResponse,
} from "@/app/actions/messages-content/scripts/action";
import { useFetchProjectCallTypes } from "@/app/hooks/fetchs/calls/useFetchProjectCallTypes";
import {
  buildScriptQueryKey,
  useFetchScript,
} from "@/app/hooks/fetchs/messages-content/useFetchScript";
import { ModalStore } from "@/app/stores/ModalStore";
import { ProjectStore } from "@/app/stores/ProjectStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { StandardCallType } from "@/app/types/call/StandardCallType";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { ScriptBootstrap } from "@/app/types/messages-content/ScriptBootstrap";
import {
  ScriptBlockData,
  ScriptData,
  ScriptItemInput,
  ScriptWorkspace,
} from "@/app/types/messages-content/ScriptData";
import {
  SCRIPT_STEPS,
  STANDARD_SCRIPT_BLOCKS,
} from "@/app/types/messages-content/StandardScriptBlock";
import { UCookie } from "@/app/utils/UCookie";
import { UScriptCallMode } from "@/app/utils/UScriptCallMode";
import { Limits } from "@/app/vars/Limits";
import { CookieKey, WORKSPACE_COOKIE_MAX_AGE } from "@/app/vars/Storage";
import {
  buildCallTypeOptions,
  buildOptimisticWorkspace,
  buildScriptBlocks,
  countCustomCallTypes,
  readSourceItems,
  withBlockBody,
  withBlockItems,
  withItemForInsight,
  withItemResponse,
} from "./Scripts.logic";
import styles from "./Scripts.module.css";

type Props = DefaultProps & {
  avatarId?: number;
  isConnected: boolean;
  bootstrap: ScriptBootstrap | null;
};

export default function Scripts(props: Props) {
  const T = useT();
  const { params, avatarId, isConnected, bootstrap } = props;

  const projectId = ProjectStore((state) => state.currentProject?.id);
  const queryClient = useQueryClient();
  const canEditContent = useCanEditContent();
  const canManageWorkspace = useCanManageWorkspace();

  const [callType, setCallType] = useState<string>(
    bootstrap?.callType ?? StandardCallType.DISCOVERY,
  );
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isCallMode, setIsCallMode] = useState(false);
  const [isRestored, setIsRestored] = useState(!!bootstrap);

  const { data: projectCallTypes } = useFetchProjectCallTypes({
    projectId,
    enabled: isConnected && !!projectId,
  });

  const callTypes = projectCallTypes ?? [];

  const { data: workspace, isFetching } = useFetchScript({
    avatarId,
    callType,
    enabled: isConnected && !!avatarId && isRestored,
    initialData:
      bootstrap && bootstrap.avatarId === avatarId && bootstrap.callType === callType
        ? bootstrap.data
        : undefined,
  });

  useEffect(() => {
    if (!bootstrap) {
      const stored = UCookie.read(CookieKey.MESSAGES_CONTENT_SELECTED_CALL_TYPE);
      if (stored) {
        setCallType(stored);
      }
      setIsRestored(true);
    }
    setIsCallMode(UScriptCallMode.read());
  }, [bootstrap]);

  const handleOpenQuotes = (insightId: number, label: string) => {
    if (!avatarId) {
      return;
    }
    ModalStore.getState().openScriptInsightQuotes(params, avatarId, insightId, label);
  };

  const toggleCallMode = (value: boolean) => {
    setIsCallMode(value);
    UScriptCallMode.write(value);
  };

  const selectCallType = (value: string) => {
    setCallType(value);
    UCookie.write(CookieKey.MESSAGES_CONTENT_SELECTED_CALL_TYPE, value, WORKSPACE_COOKIE_MAX_AGE);
  };

  const atCallTypeLimit = countCustomCallTypes(callTypes) >= Limits.MAX_PROJECT_CUSTOM_CALL_TYPES;

  const isCustomCallType = !Object.values(StandardCallType).includes(callType as StandardCallType);
  const canManageCallType = isConnected && canManageWorkspace;

  const handleAddCallType = () => {
    if (!projectId) {
      return;
    }
    ModalStore.getState().openAddCallType(params, projectId, (name) => selectCallType(name));
  };

  const handleEditCallType = () => {
    if (!projectId) {
      return;
    }
    ModalStore.getState().openEditCallType(params, projectId, callType, (name) =>
      selectCallType(name),
    );
  };

  const handleDeleteCallType = () => {
    if (!projectId) {
      return;
    }
    ModalStore.getState().openDeleteCallType(params, projectId, callType, () =>
      selectCallType(StandardCallType.DISCOVERY),
    );
  };

  const keepsRefreshHint = workspace?.canRefreshSources ?? false;

  const blockSuggestions = (type: string) => {
    const suggestion = STANDARD_SCRIPT_BLOCKS.find((shape) => shape.type === type)?.suggestion;
    return suggestion ? readSourceItems(workspace, suggestion) : [];
  };

  const blocks = buildScriptBlocks(workspace);

  const applySaved = (saved: ScriptData, canRefreshSources: boolean) => {
    if (!avatarId) {
      return;
    }
    queryClient.setQueryData(buildScriptQueryKey(avatarId, callType), {
      script: saved,
      sources: workspace?.sources ?? [],
      canRefreshSources,
    });
  };

  const save = async (
    nextBlocks: ScriptBlockData[],
    request: (id: number) => Promise<ScriptData>,
  ) => {
    if (!avatarId) {
      return;
    }
    const key = buildScriptQueryKey(avatarId, callType);
    const previous = queryClient.getQueryData<ScriptWorkspace>(key);
    queryClient.setQueryData(key, buildOptimisticWorkspace(workspace, nextBlocks, callType));

    try {
      applySaved(await request(avatarId), keepsRefreshHint);
    } catch {
      queryClient.setQueryData(key, previous);
    }
  };

  const handleSaveBody = (blockType: string, body: string) =>
    save(withBlockBody(blocks, blockType, body), (id) =>
      saveScriptBlockBody(id, callType, blockType, body),
    );

  const handleSaveItems = (blockType: string, items: ScriptItemInput[]) =>
    save(withBlockItems(blocks, blockType, items), (id) =>
      saveScriptBlockItems(id, callType, blockType, items),
    );

  const handleSaveForInsight = (blockType: string, insightId: number, label: string) =>
    save(withItemForInsight(blocks, blockType, insightId, label), (id) =>
      saveScriptItemForInsight(id, callType, blockType, insightId, label),
    );

  const handleSaveResponse = (blockType: string, label: string, response: string) =>
    save(withItemResponse(blocks, blockType, label, response), (id) =>
      saveScriptItemResponse(id, callType, blockType, label, response),
    );

  const handleRefreshSources = async () => {
    if (!avatarId) {
      return;
    }
    setIsRefreshing(true);
    try {
      applySaved(await refreshScriptSources(avatarId, callType), false);
    } finally {
      setIsRefreshing(false);
    }
  };

  const canEdit = isConnected && canEditContent && !isCallMode;
  const showSourcesBanner = canEdit && !!workspace?.canRefreshSources;

  return (
    <div className={styles.card}>
      {showSourcesBanner && (
        <div className={styles.updateBanner}>
          <p className={styles.updateText}>{T(TKey.MESSAGES_CONTENT_SCRIPT_SOURCES_UPDATED)}</p>
          <Button
            text={T(TKey.MESSAGES_CONTENT_SCRIPT_SOURCES_REFRESH_CTA) as string}
            type={ButtonType.PRIMARY}
            isLink={false}
            onClick={handleRefreshSources}
            disabled={isRefreshing}
            loading={isRefreshing}
            min={true}
          />
        </div>
      )}

      <div className={styles.cardTop}>
        <div className={styles.callType}>
          <SelectInput
            className={styles.callTypeSelect}
            label=""
            data={{ callType }}
            dataKey="callType"
            options={buildCallTypeOptions(T, callTypes)}
            onChange={(d) => selectCallType(d.callType)}
          />

          {canManageCallType && !isCallMode && isCustomCallType && (
            <div className={styles.menuAction}>
              <ActionMenu
                items={[
                  {
                    label: T(TKey.CALL_TYPE_EDIT) as string,
                    icon: <IconEdit width={16} height={16} />,
                    onClick: handleEditCallType,
                  },
                  {
                    label: T(TKey.CALL_TYPE_DELETE_CONFIRM) as string,
                    icon: <IconTrash size={16} />,
                    danger: true,
                    onClick: handleDeleteCallType,
                  },
                ]}
              />
            </div>
          )}

          {canManageCallType && !isCallMode && !atCallTypeLimit && (
            <button
              type="button"
              className={styles.addBtn}
              aria-label={T(TKey.CALL_TYPE_ADD) as string}
              onClick={handleAddCallType}
            >
              <IconPlus />
            </button>
          )}

          {isConnected && (
            <ToggleInput
              className={styles.callModeToggle}
              checked={isCallMode}
              onToggle={toggleCallMode}
              label={T(TKey.MESSAGES_CONTENT_SCRIPT_CALL_MODE) as string}
            />
          )}
        </div>
      </div>

      {isConnected && !workspace && (!isRestored || isFetching) ? (
        <div className={styles.loaderContainer}>
          <PointsLoader color="var(--color-green-3)" delay={Limits.PROSPECT_SEARCH_LOADER_DELAY} />
        </div>
      ) : (
        <div className={styles.blocks}>
          {SCRIPT_STEPS.map((step, stepIndex) => (
            <ScriptStep
              key={step.join("-")}
              index={stepIndex}
              isLast={stepIndex === SCRIPT_STEPS.length - 1}
              isSplit={step.length > 1}
            >
              {step.map((type) => {
                const block = blocks.find((item) => item.type === type);
                if (!block) {
                  return null;
                }
                return (
                  <ScriptBlock
                    key={block.type}
                    block={block}
                    suggestions={blockSuggestions(block.type)}
                    isCallMode={isCallMode}
                    isPlaceholder={!isConnected}
                    onOpenQuotes={handleOpenQuotes}
                    onSaveBody={canEdit ? (body) => handleSaveBody(block.type, body) : undefined}
                    onSaveItems={
                      canEdit ? (items) => handleSaveItems(block.type, items) : undefined
                    }
                    onSaveForInsight={
                      canEdit
                        ? (insightId, label) => handleSaveForInsight(block.type, insightId, label)
                        : undefined
                    }
                    onSaveResponse={
                      canEdit
                        ? (label, response) => handleSaveResponse(block.type, label, response)
                        : undefined
                    }
                  />
                );
              })}
            </ScriptStep>
          ))}
        </div>
      )}

      {!isConnected && (
        <div className={styles.actions}>
          <Button
            text={T(TKey.MESSAGES_CONTENT_SCRIPT_GENERATE_CTA) as string}
            type={ButtonType.PRIMARY}
            isLink={false}
            min={true}
            onClick={() => ModalStore.getState().openSignUp(params)}
          />
        </div>
      )}
    </div>
  );
}
